import { useSelector, useDispatch } from 'react-redux';

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useEffect, useState } from 'react';
import { changeTeam, removeTeam, updateTeamStatus } from '../../../features/bracket/bracketSlice';
import { setAlertError, setAlertSuccess } from '../../../features/alert/alertSlice';

const NodePlayer = ({ team, bracket, match, onUpdate }) => {
  const dispatch = useDispatch();
  const event = useSelector((state) => state.event.event);
  const loading = useSelector((state) => state.bracket.updateLoading);
  const [openStatusChange, setOpenStatusChange] = useState(false);
  const [openTeamChange, setOpenTeamChange] = useState(false);
  const [openRemove, setOpenRemove] = useState(false);

  const [status, setStatus] = useState(team?.status || null);
  const [selectedTeam, setSelectedTeam] = useState(team || null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // A slot filled by the bracket itself (a section winner or a match winner)
  // is not a team yet. It must not be turned into a walk over or a team by
  // hand, because the automatic fill matches on the placeholder id.
  const isPlaceholder = Boolean(
    team &&
      (team.id?.startsWith('section-') ||
        team.id?.startsWith('winner-of-') ||
        team.id?.startsWith('tbd-'))
  );

  const getStatus = (status) => {
    switch (status) {
      case 'WALK_OVER':
        return <Chip label="Walk Over" color="warning" size="small" sx={{ mx: 1 }} />;
      case 'NO_SHOW':
        return <Chip label="No Show" color="error" size="small" sx={{ mx: 1 }} />;
      default:
        return <Chip label="Playing" color="success" size="small" sx={{ mx: 1 }} />;
    }
  };

  const handleStatusChange = (event) => {
    setStatus(event.target.value);
  };

  const handleTeamChange = (change_event) => {
    const sTeam = event.teams.find((t) => t.id === change_event.target.value);

    setSelectedTeam(sTeam);
  };

  const handleRemove = () => {
    dispatch(removeTeam({ bracketId: bracket.id, matchId: match.id, teamId: team.id }))
      .unwrap()
      .then(() => {
        dispatch(setAlertSuccess('Player removed successfully'));
        setOpenRemove(false);
        onUpdate(match);
      })
      .catch((err) => {
        dispatch(setAlertError('Error removing player'));
        setOpenRemove(false);
      });
  };

  const handleStatusUpdate = () => {
    dispatch(
      updateTeamStatus({
        bracketId: bracket.id,
        matchId: match.id,
        teamId: team.id,
        status
      })
    )
      .unwrap()
      .then((updatedBracket) => {
        if (!updatedBracket || updatedBracket.error) {
          dispatch(setAlertError('Error updating status'));
          setOpenStatusChange(false);
          return;
        }

        const updatedMatch = updatedBracket.matches.find((m) => m.id === match.id);

        dispatch(setAlertSuccess('Status updated successfully'));
        setOpenStatusChange(false);
        onUpdate(updatedMatch);
      });
  };

  const handleTeamUpdate = () => {
    console.log('Selected team', selectedTeam);
    console.log('Team', team);
    dispatch(
      changeTeam({
        bracketId: bracket.id,
        matchId: match.id,
        teamId: team ? team.id : null,
        newTeam: selectedTeam
      })
    )
      .unwrap()
      .then(() => {
        dispatch(setAlertSuccess('Player changed successfully'));
        setOpenTeamChange(false);
        onUpdate(match);
      });
  };

  return (
    <>
      {team ? (
        isPlaceholder ? (
          <Box
            display={'flex'}
            flexDirection={'column'}
            justifyContent={'center'}
            alignItems={'flex-start'}
            gap={0.5}>
            <Typography variant="body2">{team.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              Fills automatically when that section or match is decided.
            </Typography>
          </Box>
        ) : (
          <Box
            display={'flex'}
            flexDirection={!isMobile ? 'row' : 'column'}
            justifyContent={'space-between'}
            alignItems={'center'}
            gap={2}>
            <Typography variant="body2">
              {team.name} {getStatus(team.status)}
            </Typography>
            <Box display={'flex'} gap={1}>
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                onClick={() => setOpenStatusChange(true)}>
                Update Status
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                onClick={() => setOpenTeamChange(true)}>
                Change
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => setOpenRemove(true)}>
                Remove
              </Button>
            </Box>
          </Box>
        )
      ) : (
        <Box
          display={'flex'}
          flexDirection={!isMobile ? 'row' : 'column'}
          justifyContent={'space-between'}
          alignItems={'center'}
          gap={2}>
          <Typography variant="body2">No Player Selected</Typography>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={() => setOpenTeamChange(true)}>
            Select
          </Button>
        </Box>
      )}

      <Dialog
        open={openStatusChange}
        onClose={() => setOpenStatusChange(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}>
        <DialogTitle>Change status of {team?.name}?</DialogTitle>
        <DialogContent>
          <Select name="status" value={status || 'PLAYING'} fullWidth onChange={handleStatusChange}>
            <MenuItem value="WALK_OVER">Walk Over</MenuItem>
            <MenuItem value="NO_SHOW">No Show</MenuItem>
            <MenuItem value="PLAYING">Playing</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions>
          {loading ? (
            <Button disabled>Loading...</Button>
          ) : (
            <>
              <Button onClick={() => setOpenStatusChange(false)}>Cancel</Button>
              <Button
                onClick={() => {
                  handleStatusUpdate();
                }}>
                Confirm
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={openTeamChange}
        onClose={() => setOpenTeamChange(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}>
        <DialogTitle>Change player</DialogTitle>
        <DialogContent>
          <Select
            name="team"
            defaultValue={setSelectedTeam.id}
            fullWidth
            onChange={handleTeamChange}>
            {event.teams.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          {loading ? (
            <Button disabled>Loading...</Button>
          ) : (
            <>
              <Button onClick={() => setOpenTeamChange(false)}>Cancel</Button>
              <Button onClick={() => handleTeamUpdate()}>Confirm</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={openRemove}
        onClose={() => setOpenRemove(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}>
        <DialogTitle>Remove Player</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to remove {team?.name}?</DialogContentText>
        </DialogContent>
        <DialogActions>
          {loading ? (
            <Button disabled>Loading...</Button>
          ) : (
            <>
              <Button onClick={() => setOpenRemove(false)}>Cancel</Button>
              <Button onClick={() => handleRemove()}>Confirm</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

const NodePlayers = ({ matchNode, bracket, onUpdate }) => {
  const [player1, setPlayer1] = useState();
  const [player2, setPlayer2] = useState();

  useEffect(() => {
    const { participants } = matchNode;

    if (participants.length > 0) {
      setPlayer1(participants[0]);
    } else {
      setPlayer1(null);
    }

    if (participants.length > 1) {
      setPlayer2(participants[1]);
    } else {
      setPlayer2(null);
    }
  }, [matchNode]);

  return (
    <Box display={'flex'} flexDirection={'column'} gap={2} mt={2}>
      <NodePlayer
        team={player1}
        match={matchNode}
        bracket={bracket}
        onUpdate={(mn) => onUpdate(mn)}
      />
      <NodePlayer
        team={player2}
        match={matchNode}
        bracket={bracket}
        onUpdate={(mn) => onUpdate(mn)}
      />
    </Box>
  );
};

export default NodePlayers;
